import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import {
  Inbox,
  Bell,
  Ticket,
  BookOpen,
  Users,
  MessageSquare,
  FileText,
  HelpCircle,
  LogOut,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Settings,
  Shield,
  BarChart3,
  FolderOpen,
  Building2,
  Layers,
  Clock,
  UserPlus,
  PieChart,
  Globe,
  History,
  Star as StarIcon,
  PlusCircle,
  Sun,
  Moon,
  BellOff
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useBranding } from '../context/BrandingContext';
import { useTheme } from '../context/ThemeContext';
import { useSidebar } from '../context/SidebarContext';
import NotificationBadge from './NotificationBadge';
import logo from '../images/logo_12.png';
import { resolveMediaUrl } from '../lib/mediaUrl';

const Sidebar = ({ userRole: propUserRole, onCloseMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { notificationsMuted, setNotificationsMuted } = useNotifications();
  const { brandDisplayName, portalName, logoSrc } = useBranding();
  const { toggleTheme, isDark } = useTheme();
  
  // Use propUserRole if provided, otherwise use user's role from context
  // This ensures admin/employee see admin sidebar, customers see customer sidebar
  const userRole = propUserRole || user?.role || 'customer';
  const effectiveBrandDisplayName = user?.tenant_name || brandDisplayName;
  const effectivePortalName = user?.tenant_portal_name || portalName;
  const effectiveLogoSrc = resolveMediaUrl(user?.tenant_logo_url || logoSrc || '');
  const [logoBroken, setLogoBroken] = useState(false);

  useEffect(() => {
    setLogoBroken(false);
  }, [effectiveLogoSrc]);

  const resolvedLogoUrl = logoBroken ? logo : (effectiveLogoSrc || logo);
  
  // Use global sidebar collapsed state from context so all toggles stay in sync
  const { sidebarCollapsed: collapsed, toggleSidebar } = useSidebar();
  const isMobileDrawer = !!onCloseMobile;
  const effectiveCollapsed = isMobileDrawer ? false : collapsed;
  const [expandedSections, setExpandedSections] = useState({
    platform: true,
    incidents: true,
    service: true,
    management: false,
    analytics: false,
    administration: false
  });
  const scrollContainerRef = useRef(null);
  const savedScrollPositionRef = useRef(null);
  const isPreservingScrollRef = useRef(false);
  
  // Load saved scroll position from sessionStorage on mount
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('sidebarScrollPosition');
    if (savedScroll && scrollContainerRef.current) {
      const scrollPosition = parseInt(savedScroll, 10);
      // Restore after a short delay to ensure DOM is ready
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPosition;
        }
      }, 100);
    }
  }, []);
  
  // Save scroll position to sessionStorage whenever it changes (debounced)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    let scrollTimeout;
    const handleScroll = () => {
      // Debounce scroll saving to avoid too many writes
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Only save if we're not currently preserving scroll (to avoid conflicts)
        if (!isPreservingScrollRef.current) {
          sessionStorage.setItem('sidebarScrollPosition', container.scrollTop.toString());
        }
      }, 150);
    };
    
    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      clearTimeout(scrollTimeout);
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Restore scroll position after expandedSections changes
  useEffect(() => {
    if (isPreservingScrollRef.current && savedScrollPositionRef.current !== null && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const savedPosition = savedScrollPositionRef.current;
      
      // Restore scroll position after a short delay to allow DOM to update
      const restoreScroll = () => {
        if (container) {
          container.scrollTop = savedPosition;
        }
      };
      
      // Use requestAnimationFrame for smooth restoration
      requestAnimationFrame(() => {
        restoreScroll();
        // Clear the flag immediately after restoring to allow normal scrolling
        setTimeout(() => {
          isPreservingScrollRef.current = false;
          savedScrollPositionRef.current = null;
        }, 50);
      });
    }
  }, [expandedSections]);

  const toggleSection = (section, buttonElement) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    // Save current scroll position BEFORE state change
    savedScrollPositionRef.current = container.scrollTop;
    isPreservingScrollRef.current = true;
    
    // Toggle section
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isActive = (path) => {
    const [basePath, queryString] = path.split('?');
    const currentPath = location.pathname;
    const urlParams = new URLSearchParams(location.search);
    
    // First, check if the pathname matches exactly
    if (currentPath !== basePath) {
      // If current path is a child route (e.g., /admin/requests/approved is child of /admin/requests)
      // Don't highlight parent routes when on child routes
      if (currentPath.startsWith(basePath + '/')) {
        return false;
      }
      // Path doesn't match at all
      return false;
    }
    
    // Pathname matches exactly, now check query parameters
    if (queryString) {
      // This path requires specific query parameters
      const pathParams = new URLSearchParams(queryString);
      // All query parameters in the path definition must match current URL
      for (const [key, value] of pathParams.entries()) {
        if (urlParams.get(key) !== value) {
          return false;
        }
      }
      // All required query params match
      return true;
    } else {
      // Path has no query parameters defined
      // Highlight only if current URL has no query params, or only has filter=all (which is equivalent to no filter)
      const filterValue = urlParams.get('filter');
      if (location.search === '' || location.search === '?') {
        return true;
      }
      // If only filter=all is present, treat it as no filter (should highlight)
      if (urlParams.toString() === 'filter=all' && filterValue === 'all') {
        return true;
      }
      // Has other query params, don't highlight
      return false;
    }
  };

  // ServiceNow-style navigation structure
  const navigationSections = userRole === 'super_admin'
    ? [
        {
          id: 'platform',
          label: 'Platform',
          icon: Shield,
          items: [
            { icon: Building2, label: 'Tenants', path: '/super-admin/tenants', badge: null },
            { icon: Ticket, label: 'Global ticket control', path: '/super-admin/tickets', badge: null },
            { icon: Layers, label: 'Assignment Groups', path: '/super-admin/assignment-groups', badge: null },
            { icon: History, label: 'System Logs', path: '/super-admin/logs', badge: null },
            { icon: BarChart3, label: 'System Log Report', path: '/super-admin/logs/report', badge: null },
            { icon: PlusCircle, label: 'Add New Tenant', path: '/super-admin/tenants/new', badge: null },
          ]
        }
      ]
    : userRole === 'admin'
    ? [
        {
          id: 'incidents',
          label: 'Tickets',
          icon: Ticket,
          items: [
            { icon: Inbox, label: 'All Tickets', path: '/admin/tickets', badge: null },
            { icon: Clock, label: 'Pending', path: '/admin/tickets?filter=pending', badge: null },
            { icon: Ticket, label: 'My Tickets', path: '/admin/tickets?filter=my', badge: null },
            { icon: StarIcon, label: 'Favorites', path: '/admin/tickets?filter=favorites', badge: null },
            { icon: History, label: 'Recent', path: '/admin/tickets?filter=recent', badge: null },
          ]
        },
        {
          id: 'service',
          label: 'Service Catalog',
          icon: FolderOpen,
          items: [
            { icon: PlusCircle, label: 'Create Ticket', path: '/tickets/create', badge: null },
            // { icon: ClipboardList, label: 'My Requests', path: '/admin/requests', badge: null },
          ]
        },
        // {
        //   id: 'management',
        //   label: 'Management',
        //   icon: Settings,
        //   items: [
        //     { icon: AlertTriangle, label: 'Problem Management', path: '/admin/problems', badge: null },
        //     { icon: RefreshCw, label: 'Change Management', path: '/admin/changes', badge: null },
        //     { icon: Database, label: 'Configuration Items', path: '/admin/cmdb', badge: null },
        //     { icon: Network, label: 'Service Mapping', path: '/admin/service-map', badge: null },
        //     { icon: Shield, label: 'SLA Management', path: '/admin/sla', badge: null },
        //     { icon: Workflow, label: 'Workflows', path: '/admin/workflows', badge: null },
        //     { icon: Zap, label: 'Automation', path: '/admin/automation', badge: null },
        //   ]
        // },
        {
          id: 'analytics',
          label: 'Reports ',
          icon: BarChart3,
          items: [
            { icon: PieChart, label: 'Reports', path: '/admin/reports', badge: null },
            // { icon: TrendingUp, label: 'Performance', path: '/admin/reports/performance', badge: null },
            // { icon: Activity, label: 'Metrics', path: '/admin/metrics', badge: null },
            // { icon: Download, label: 'Exports', path: '/admin/exports', badge: null },
            // { icon: Calendar, label: 'Scheduled Reports', path: '/admin/reports/scheduled', badge: null },
          ]
        },
        {
          id: 'knowledge',
          label: 'Knowledge',
          icon: BookOpen,
          items: [
            { icon: BookOpen, label: 'Knowledge Base', path: '/admin/knowledge', badge: null },
            { icon: FileText, label: 'My Articles', path: '/admin/knowledge/my-articles', badge: null },
            { icon: StarIcon, label: 'Favorites', path: '/admin/knowledge/favorites', badge: null },
            { icon: HelpCircle, label: 'FAQs', path: '/admin/knowledge/faqs', badge: null },
            // { icon: Tag, label: 'Categories', path: '/admin/knowledge/categories', badge: null },
          ]
        },
        {
          id: 'administration',
          label: 'Administration',
          icon: Settings,
          items: [
            { icon: Users, label: 'Users & Groups', path: '/admin/users', badge: null },
            { icon: Globe, label: 'Domain Access', path: '/admin/domains', badge: null },
            // { icon: Shield, label: 'Roles & Permissions', path: '/admin/roles', badge: null },
            // { icon: Settings, label: 'System Settings', path: '/admin/settings', badge: null },
            // { icon: Mail, label: 'Email Configuration', path: '/admin/settings/email', badge: null },
            // { icon: Key, label: 'API Keys', path: '/admin/settings/api', badge: null },
            // { icon: Lock, label: 'Security', path: '/admin/settings/security', badge: null },
            // { icon: Eye, label: 'Audit Logs', path: '/admin/audit-logs', badge: null },
            // { icon: Globe, label: 'Integrations', path: '/admin/integrations', badge: null },
          ]
        },
        {
          id: 'communication',
          label: 'Communication',
          icon: MessageSquare,
          items: [
            { icon: Bell, label: 'Notifications', path: '/admin/notifications', badge: null },
            // { icon: MessageSquare, label: 'Messages', path: '/admin/messages', badge: null },
            // { icon: Phone, label: 'Chat', path: '/admin/chat', badge: null },
            // { icon: Mail, label: 'Email Templates', path: '/admin/email-templates', badge: null },
          ]
        }
      ]
    : userRole === 'employee'
    ? [
        {
          id: 'incidents',
          label: 'Tickets',
          icon: Ticket,
          items: [
            { icon: Clock, label: 'Pending', path: '/admin/tickets?filter=pending', badge: null },
            { icon: Ticket, label: 'My Tickets', path: '/admin/tickets?filter=my', badge: null },
            { icon: UserPlus, label: 'Unassigned', path: '/admin/tickets/unassigned', badge: null },
            { icon: StarIcon, label: 'Favorites', path: '/admin/tickets?filter=favorites', badge: null },
            { icon: History, label: 'Recent', path: '/admin/tickets?filter=recent', badge: null },
          ]
        },
        {
          id: 'service',
          label: 'Service Catalog',
          icon: FolderOpen,
          items: [
            { icon: PlusCircle, label: 'Create Request', path: '/tickets/create', badge: null },
            // { icon: ClipboardList, label: 'My Requests', path: '/admin/requests', badge: null },
            // { icon: FileCheck, label: 'Service Items', path: '/admin/service-items', badge: null },
          ]
        },
        {
          id: 'knowledge',
          label: 'Knowledge',
          icon: BookOpen,
          items: [
            { icon: BookOpen, label: 'Knowledge Base', path: '/admin/knowledge', badge: null },
            { icon: StarIcon, label: 'Favorites', path: '/admin/knowledge/favorites', badge: null },
            { icon: HelpCircle, label: 'FAQs', path: '/admin/knowledge/faqs', badge: null },
          ]
        },
        {
          id: 'communication',
          label: 'Communication',
          icon: MessageSquare,
          items: [
            { icon: Bell, label: 'Notifications', path: '/admin/notifications', badge: null },
            // { icon: MessageSquare, label: 'Messages', path: '/admin/messages', badge: null },
          ]
        }
      ]
    : userRole === 'global_support'
    ? [
        {
          id: 'incidents',
          label: 'Tickets',
          icon: Ticket,
          items: [
            { icon: Inbox, label: 'All Tickets', path: '/admin/tickets', badge: null },
            { icon: Clock, label: 'Pending', path: '/admin/tickets?filter=pending', badge: null },
            { icon: StarIcon, label: 'Favorites', path: '/admin/tickets?filter=favorites', badge: null },
            { icon: History, label: 'Recent', path: '/admin/tickets?filter=recent', badge: null },
          ]
        },
        {
          id: 'analytics',
          label: 'Reports',
          icon: BarChart3,
          items: [{ icon: PieChart, label: 'Reports', path: '/admin/reports', badge: null }]
        },
        {
          id: 'communication',
          label: 'Communication',
          icon: MessageSquare,
          items: [{ icon: Bell, label: 'Notifications', path: '/admin/notifications', badge: null }]
        }
      ]
    : [
        {
          id: 'incidents',
          label: 'Tickets',
          icon: Ticket,
          items: [
            { icon: Inbox, label: 'All Tickets', path: '/customer/tickets', badge: null },
            { icon: StarIcon, label: 'Favorites', path: '/customer/tickets?filter=favorites', badge: null },
            { icon: History, label: 'Recent', path: '/customer/tickets?filter=recent', badge: null },
          ]
        },
        {
          id: 'service',
          label: 'Service Catalog',
          icon: FolderOpen,
          items: [
            // For customers, only show the main Create Ticket entry.
            // "My Requests" and "Service Items" are intentionally hidden.
            { icon: PlusCircle, label: 'Create Ticket', path: '/tickets/create', badge: null }
          ]
        },
        {
          id: 'knowledge',
          label: 'Knowledge Base',
          icon: BookOpen,
          items: [
            { icon: BookOpen, label: 'Articles', path: '/customer/knowledge', badge: null },
            { icon: HelpCircle, label: 'FAQs', path: '/customer/knowledge/faqs', badge: null },
            { icon: StarIcon, label: 'Favorites', path: '/customer/knowledge/favorites', badge: null },
          ]
        },
        {
          id: 'communication',
          label: 'Communication',
          icon: MessageSquare,
          items: [
            { icon: Bell, label: 'Notifications', path: '/customer/notifications', badge: null },
            // { icon: MessageSquare, label: 'Messages', path: '/customer/messages', badge: null },
          ]
        }
      ];

  const toggleCollapsed = () => {
    toggleSidebar();
  };

  const homePath = userRole === 'super_admin'
    ? '/super-admin/tenants'
    : userRole === 'admin'
    ? '/admin/dashboard'
    : userRole === 'employee'
    ? '/employee/dashboard'
    : userRole === 'global_support'
    ? '/admin/tickets'
    : '/customer/dashboard';

  const homeLabel = userRole === 'super_admin' ? 'Tenant Console' : 'Dashboard';

  const handleLinkClick = (e) => {
    // Save scroll position before navigation (to sessionStorage for persistence)
    const container = scrollContainerRef.current;
    if (container) {
      const scrollPosition = container.scrollTop;
      savedScrollPositionRef.current = scrollPosition;
      isPreservingScrollRef.current = true;
      // Also save to sessionStorage so it persists across route changes
      sessionStorage.setItem('sidebarScrollPosition', scrollPosition.toString());
    }
    
    // REMOVED: Auto-expand logic - sidebar stays collapsed when clicking links
  };

  const handleSectionToggle = (sectionId, event) => {
    // Prevent default link behavior if it's a link
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const container = scrollContainerRef.current;
    const buttonElement = event?.currentTarget;
    
    // If sidebar is collapsed, expand it first, then open this specific section
    if (collapsed) {
      toggleSidebar(); // expand sidebar
      setTimeout(() => {
        // Optionally preserve scroll if needed in future
        if (container) {
          savedScrollPositionRef.current = container.scrollTop;
          isPreservingScrollRef.current = true;
        }
        setExpandedSections(prev => ({
          ...prev,
          [sectionId]: true
        }));
      }, 100);
    } else {
      // Normal behavior: just toggle the section
      toggleSection(sectionId, buttonElement);
    }
  };

  return (
    <div
      className={`bg-gradient-sidebar border-r border-border-subtle flex flex-col h-full flex-shrink-0 transition-all duration-200 ${
        isMobileDrawer ? 'w-full' : (effectiveCollapsed ? 'w-16' : 'w-64')
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo and Branding – fixed min-height avoids layout shift when the image loads */}
      <div className="min-h-[4.75rem] px-3 py-3 sm:px-4 sm:py-4 border-b border-border-subtle flex items-center justify-between gap-2">
        <button
          type="button"
          className={`flex items-center cursor-pointer min-w-0 flex-1 text-left bg-transparent border-0 p-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500/40 rounded-xl ${
            effectiveCollapsed ? 'justify-center' : 'gap-3'
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCloseMobile ? onCloseMobile() : toggleCollapsed();
          }}
          title={onCloseMobile ? 'Close menu' : (effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar')}
          aria-label={onCloseMobile ? 'Close menu' : (effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar')}
        >
          <div
            className={`relative flex shrink-0 items-center justify-center rounded-lg bg-white dark:bg-white ${
              effectiveCollapsed ? 'h-11 w-11' : 'h-14 w-14'
            }`}
          >
            <img
              key={`sidebar-logo-${user?.tenant_id ?? 'x'}-${user?.tenant_logo_url || ''}-${logoSrc || ''}`}
              src={resolvedLogoUrl}
              alt={`${effectiveBrandDisplayName} logo`}
              width={48}
              height={48}
              decoding="async"
              className={`object-contain object-center transition-all duration-300 ease-out ${
                effectiveCollapsed ? 'h-9 w-9 max-h-9 max-w-9' : 'h-12 w-12 max-h-12 max-w-12'
              }`}
              onError={() => setLogoBroken(true)}
            />
          </div>
          {!effectiveCollapsed && (
            <div className="min-w-0 flex-1 transition-opacity duration-300">
              <p className="text-sm font-semibold text-text-primary truncate tracking-tight">
                {effectiveBrandDisplayName}
              </p>
              <span className="text-xs text-text-secondary">
                {userRole === 'super_admin'
                  ? 'Platform Admin'
                  : userRole === 'admin'
                    ? 'Agent Admin'
                    : userRole === 'employee'
                      ? 'Agent Portal'
                      : 'Customer Portal'}
              </span>
            </div>
          )}
        </button>
        {!effectiveCollapsed && (
          <div className="flex items-center gap-2">
            <NotificationBadge />
            {!onCloseMobile && (
              <button
                onClick={toggleCollapsed}
                className="p-1.5 rounded hover:bg-sidebar-gradient-top text-text-secondary flex-shrink-0"
                title="Collapse sidebar"
                aria-label="Collapse sidebar"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Navigation Menu with Collapsible Sections */}
      <div 
        ref={scrollContainerRef} 
        className="flex-1 overflow-y-auto py-4 min-h-0"
        style={{ 
          scrollBehavior: 'auto'
        }}
      >
        <nav className={`space-y-1 ${effectiveCollapsed ? 'px-1.5' : 'px-3'}`}>
          {/* Dashboard - Standalone Item */}
          <div className="mb-2">
            <Link
              to={homePath}
              onClick={handleLinkClick}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                isActive(homePath)
                  ? 'bg-gradient-primary text-text-primary shadow-button-hover'
                  : 'text-text-secondary hover:bg-sidebar-gradient-top hover:text-text-primary'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              {!effectiveCollapsed && <span className="text-sm">{homeLabel}</span>}
            </Link>
          </div>
          
          {navigationSections.map((section) => {
            const SectionIcon = section.icon;
            const isExpanded = expandedSections[section.id];
            const hasActiveItem = section.items.some(item => isActive(item.path));

            return (
              <div key={section.id} className="mb-2">
                {/* Section Header */}
                <button
                  onClick={(e) => handleSectionToggle(section.id, e)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    hasActiveItem
                      ? 'bg-sidebar-gradient-top text-text-primary'
                      : 'text-text-secondary hover:bg-sidebar-gradient-top/50 hover:text-text-primary'
                  }`}
                >
                  <div className="flex items-center space-x-2 justify-center">
                    <SectionIcon className="w-4 h-4" />
                    {!effectiveCollapsed && (
                      <span className="text-xs font-semibold uppercase tracking-wider">
                        {section.label}
                      </span>
                    )}
                  </div>
                  {!effectiveCollapsed && (
                    isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )
                  )}
                </button>

                {/* Section Items */}
                {isExpanded && !effectiveCollapsed && (
                  <div id={`section-${section.id}`} className="mt-1 ml-4 space-y-0.5" role="group" aria-label={`${section.label} items`}>
                    {section.items.map((item) => {
                      const ItemIcon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={handleLinkClick}
                          className={`flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                            active
                              ? 'bg-gradient-primary text-text-primary shadow-button-hover'
                              : 'text-text-secondary hover:bg-sidebar-gradient-top hover:text-text-primary'
                          }`}
                          aria-current={active ? 'page' : undefined}
                          aria-label={item.label}
                        >
                          <div className="flex items-center space-x-2">
                            <ItemIcon className="w-4 h-4" />
                            <span className="text-sm">{item.label}</span>
                          </div>
                          {item.badge && (
                            <span className="px-1.5 py-0.5 text-xs bg-primary-accent text-text-primary rounded">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Quick Actions Section */}
        {userRole !== 'super_admin' && (
          <div className="px-3 mt-6 border-t border-border-subtle pt-4">
            {!effectiveCollapsed && (
              <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider px-3 mb-2">
                Quick Actions
              </h3>
            )}
            <div className="space-y-1">
              <Link
                to="/tickets/create"
                onClick={handleLinkClick}
                className="flex items-center justify-center space-x-2 px-3 py-2 text-sm text-primary-accent hover:text-primary-light hover:bg-sidebar-gradient-top/30 rounded-lg transition-colors"
              >
                <PlusCircle className="w-4 h-4" />
                {!effectiveCollapsed && <span>Create Ticket</span>}
              </Link>
            </div>
          </div>
        )}

        {/* Pinned/Favorites Section */}
        {/* <div className="px-3 mt-4">
          {!effectiveCollapsed && (
            <div className="flex items-center justify-between px-3 mb-2">
              <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Pinned
              </h3>
              <button className="text-xs text-primary-accent hover:text-primary-light">
                Manage
              </button>
            </div>
          )}
          <div className="space-y-1">
            <button className="flex items-center justify-center space-x-2 px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-sidebar-gradient-top/30 rounded-lg transition-colors w-full">
              <Bookmark className="w-3 h-3" />
              {!effectiveCollapsed && <span>Add to Pinned</span>}
            </button>
          </div>
        </div> */}
      </div>

      {/* Bottom Section */}
      <div className="border-t border-border-subtle p-4 space-y-3 bg-sidebar-bg">
        {/* Dark Mode Toggle */}
        <button
          onClick={toggleTheme}
          className="flex items-center justify-center space-x-2 text-sm text-text-secondary hover:text-text-primary hover:bg-sidebar-gradient-top/30 rounded-lg transition-colors w-full py-2"
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? (
            <>
              <Sun className="w-5 h-5" />
              {!effectiveCollapsed && <span>Light Mode</span>}
            </>
          ) : (
            <>
              <Moon className="w-5 h-5" />
              {!effectiveCollapsed && <span>Dark Mode</span>}
            </>
          )}
        </button>

        <button
          type="button"
          onClick={() => setNotificationsMuted(!notificationsMuted)}
          className="flex items-center justify-center space-x-2 text-sm text-text-secondary hover:text-text-primary hover:bg-sidebar-gradient-top/30 rounded-lg transition-colors w-full py-2"
          aria-pressed={notificationsMuted}
          aria-label={
            notificationsMuted
              ? 'Turn notifications on (end testing mute)'
              : 'Mute notifications for testing'
          }
          title={
            notificationsMuted
              ? 'Notifications are muted: no polling or badge. Click to resume.'
              : 'Mute notifications while testing (stops fetches and badge)'
          }
        >
          {notificationsMuted ? (
            <>
              <BellOff className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400" />
              {!effectiveCollapsed && (
                <span className="truncate text-left">Notifications off (testing)</span>
              )}
            </>
          ) : (
            <>
              <Bell className="w-5 h-5 shrink-0" />
              {!effectiveCollapsed && (
                <span className="truncate text-left">Mute notifications (testing)</span>
              )}
            </>
          )}
        </button>

        {/* Help & Support */}
        <Link
          to="/help"
          onClick={handleLinkClick}
          className="flex items-center justify-center space-x-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
          aria-label="Help & Support"
        >
          <HelpCircle className="w-5 h-5" />
          {!effectiveCollapsed && <span>Help & Support</span>}
        </Link>

        {/* User Profile */}
        <div className="flex items-center justify-between">
          <Link
            to="/profile"
            onClick={handleLinkClick}
            className="flex items-center space-x-2 flex-1 min-w-0 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs text-text-primary font-medium">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            {!effectiveCollapsed && (
              <div className="min-w-0 flex-1">
                <p className="text-xs text-text-primary font-medium truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-text-secondary truncate">{user?.email || ''}</p>
              </div>
            )}
          </Link>
          <button
            onClick={handleLogout}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-sidebar-gradient-top rounded-lg transition-colors flex-shrink-0"
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Footer */}
        {!effectiveCollapsed && (
          <div className="flex items-center justify-between text-xs">
            <p className="text-text-secondary">POWERED BY</p>
            <span className="font-semibold tenant-brand-text">{effectivePortalName}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;

